import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Result } from 'antd';

interface State {
  error: Error | null;
}

/**
 * The last line of defence for a page render: a thrown render error (the minified React #31
 * class — an object rendered as a child, a bad shape from an API) is contained to the CONTENT
 * area with the nav still alive, instead of unmounting the entire app into a blank page.
 * Resetting re-mounts the routed page; the shell never dies with its passenger.
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Console, not telemetry: the trace is for the developer console session.
    console.error('page render crashed (contained by PageErrorBoundary):', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Result
          status="error"
          title="This page hit a rendering error"
          subTitle={this.state.error.message}
          extra={
            <Button type="primary" onClick={() => this.setState({ error: null })}>
              Reload this page
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}
